import { Box, Grid } from '@material-ui/core';
import { Agent, Channel } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';

import { CustomCartAlert } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/CartAlerts/components/CustomCartAlert';
import { AgentSelector } from '@/components/shared/components/AgentSelectionComponent/components/AgentSelector';
import { ChannelSelector } from '@/components/shared/components/AgentSelectionComponent/components/ChannelSelector';
import { MerchantSelector } from '@/components/shared/components/AgentSelectionComponent/components/MerchantSelector';
import { useAgentSelectionComponent } from '@/components/shared/components/AgentSelectionComponent/hooks/useAgentSelectionComponent';
import { AgentSelectionComponentState } from '@/components/shared/components/AgentSelectionComponent/types/AgentSelectionComponentState';
import { Text } from '@/components/shared/components/Text';
import { useScreen } from '@/hooks/useScreen';
import { MerchantInStructure } from '@/types/MerchantInStructure';

export interface AgentSelectionComponentProps {
  initialValues: AgentSelectionComponentState;
  onChange: (
    selectedAgent: Agent | undefined,
    selectedChannel: Channel | undefined,
    selectedMerchant: MerchantInStructure | undefined,
  ) => void;
  error?: string | undefined;
  resetItem?: () => boolean;
  fullWidth?: boolean;
}

export const AgentSelectionComponent = ({
  initialValues,
  onChange,
  error,
  resetItem,
  fullWidth,
}: AgentSelectionComponentProps) => {
  const { isMobile, sm } = useScreen();
  const {
    isUserAgent,
    showMerchantSelector,
    showChannelSelector,
    channelSelectorDisabled,
    channels,
    selectedAgent,
    selectedChannel,
    selectedMerchant,
    setState,
  } = useAgentSelectionComponent(initialValues);

  useEffect(() => {
    if (resetItem && resetItem() === true) setState(initialValues);
  }, [resetItem]);

  useEffect(() => {
    onChange(selectedAgent, selectedChannel, selectedMerchant);
  }, [selectedAgent, selectedChannel, selectedMerchant]);

  if (isUserAgent) {
    return null;
  }

  const showErrorAlert = selectedAgent && !!error;

  const merchantComponent = <MerchantSelector selectedMerchant={selectedMerchant} setState={setState} />;

  const channelComponent = (
    <ChannelSelector
      selectedChannel={selectedChannel}
      channels={channels}
      disabled={channelSelectorDisabled}
      setState={setState}
    />
  );

  const agentComponent = (
    <>
      <AgentSelector selectedAgent={selectedAgent} selectedChannel={selectedChannel} setState={setState} />
      {showErrorAlert && (
        <Box pt={1}>
          <CustomCartAlert severity={'error'} icon={'error'}>
            <Text tId={error} />
          </CustomCartAlert>
        </Box>
      )}
    </>
  );

  if (fullWidth) {
    const columnCount = (showMerchantSelector ? 1 : 0) + (showChannelSelector ? 1 : 0) + 1;
    const columnSize = (12 / columnCount) as 4 | 6 | 12;

    return (
      <Grid container spacing={isMobile ? 2 : 4}>
        {showMerchantSelector && (
          <Grid item xs={12} sm={columnSize}>
            {merchantComponent}
          </Grid>
        )}
        {showChannelSelector && (
          <Grid item xs={12} sm={columnSize}>
            {channelComponent}
          </Grid>
        )}
        <Grid item xs={12} sm={columnSize}>
          {agentComponent}
        </Grid>
      </Grid>
    );
  }

  return (
    <Box display={'flex'} gridGap={sm ? '1.5rem' : '0.5rem'} flexWrap={sm ? 'no-wrap' : 'wrap'}>
      {showMerchantSelector && <Box width={sm ? '350px' : '100%'}>{merchantComponent}</Box>}
      {showChannelSelector && <Box width={sm ? '350px' : '100%'}>{channelComponent}</Box>}
      <Box width={sm ? '350px' : '100%'}>{agentComponent}</Box>
    </Box>
  );
};
